import React, { useMemo } from "react";
import styled from "styled-components";

import Avatar from "@ingka/avatar";
import Text from "@ingka/text";

import ContentBox from "../../../ContentBox/ContentBox";
import { SrOnly } from "../../../styles";
import { Value } from "../../../../utils/types";
import { getInitialsFromEmail } from "../../../../utils/common";
import { colourTextAndIcon1, colourTextAndIcon2 } from "@ingka/variables/colours-css";
import { fontSize75, space100 } from "@ingka/variables/design-tokens";

const UserInfoWrapper = styled.div`
	display: flex;
	gap: 0.5rem;
	align-items: center;
	margin-bottom: 1rem;
`;

const UserText = styled.div`
	display: flex;
	flex-direction: column;
	line-height: 1.25;
`;

const Name = styled.p`
	font-weight: bold;
`;

const Email = styled.p`
	margin: 0;
`;

const StyledUl = styled.ul`
	padding-left: 1rem;
	margin: 0.25rem 0 0;
`;

const Section = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
	justify-content: flex-start;
`;

const Title = styled(Text)`
	color: ${colourTextAndIcon1};
	font-weight: bold;
`;

const SectionContent = styled.div`
	color: ${colourTextAndIcon2};
	font-size: ${fontSize75};
	display: flex;
	flex-direction: column;
`;

const OverviewContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${space100};
`;

interface RequestOverviewLayoutProps {
	value: Value;
}

const DetailSection: React.FC<{ id: string; title: string; description: string | React.ReactNode }> = ({
	id,
	title,
	description,
}) => {
	const renderDescription = (description: string | React.ReactNode) => {
		if (typeof description === "string") {
			<Text tagName="span">{description}</Text>;
		}
		return description;
	};
	return (
		<Section id={`details-container--${id}`}>
			<SectionContent>
				<Title tagName="span">{title}</Title>
				{description && renderDescription(description)}
			</SectionContent>
		</Section>
	);
};

const Layout: React.FC<RequestOverviewLayoutProps> = ({ value }) => {
	const { userName, email, clientIds, plan } = value;
	const initials = useMemo(() => (email ? getInitialsFromEmail(email).toUpperCase() : ""), [email]);
	const clientIdItems = clientIds ?? [];

	const renderNoValue = () => (
		<>
			<span aria-hidden={true}>-</span>
			<SrOnly>Not available</SrOnly>
		</>
	);

	const renderItems = (items: string[], itemIdPrefix: string) => {
		if (!items.length) {
			return renderNoValue();
		}
		return (
			<StyledUl>
				{items.map((item: string, idx) => {
					const liId = `${itemIdPrefix}-${String(idx)}`;

					return (
						<li key={liId} id={liId}>
							{item}
						</li>
					);
				})}
			</StyledUl>
		);
	};

	const roleItems = useMemo(() => {
		const roles: string[] = ["Admin", "Maintainer"];
		if (value.allowReadOnly) roles.push("Read Only");
		return roles;
	}, [value.allowReadOnly]);

	const dashboardItems = useMemo(() => {
		const items: string[] = [];
		if (value.showDashboard) items.push("Global Dashboard");
		if (value.showMarketDashboard) items.push("Market Dashboard");
		return items;
	}, [value.showDashboard, value.showMarketDashboard]);

	return (
		<>
			<ContentBox>
				<h2>Request for new application</h2>

				<UserInfoWrapper>
					<Avatar screenReaderText="" text={initials} size="medium" variant="primary" aria-hidden={true} />
					<UserText>
						{userName && <Name>{userName}</Name>}
						<Email>{value.email}</Email>
					</UserText>
				</UserInfoWrapper>

				<OverviewContainer id="request-overview">
					<DetailSection id="application-id" title="Application ID:" description={value.appId || renderNoValue()} />
					<DetailSection
						id="application-name"
						title="Application Name:"
						description={value.appName || renderNoValue()}
					/>
					<DetailSection id="application-plan" title="Plan:" description={value.plan || renderNoValue()} />

					<DetailSection
						id="application-client-ids"
						title="Client ID(s):"
						description={renderItems(clientIdItems, "clientId")}
					/>

					<DetailSection
						id="application-roles"
						title="Roles for this application:"
						description={renderItems(roleItems, "role")}
					/>

					<DetailSection
						id="application-dashboards"
						title="Dashboard for this application:"
						description={renderItems(dashboardItems, "dashboard")}
					/>
				</OverviewContainer>
			</ContentBox>
		</>
	);
};

export default Layout;
